import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useGetStudentsQuery,
  useUpdateStudentMutation,
} from "../../services/students.service";
import { Student, CHIPS_COLOR, STUDENT_STATUS } from "../../types/students";
import { Document, Note, Service } from "../../types/additionalData";
import { formatDate } from "../../util/helper";
import { useGetUsersQuery } from "../../services/users.service";
import Documents from "../features/Documents";
import Services from "../features/Services";
import Loader from "../common/Loader";
import Notes from "../features/Notes";
import StudentAdditionalInfo from "./StudentAdditionalInfo";
import StudentsForm from "./StudentsForm";
import TransactionsTable from "../Transactions/Table";
import { useGetTransactionsQuery } from "../../services/tansactions.service";
import { Transaction } from "../../types/transactions";
import InvoicesTable from "../Invoices/Table";
import { Invoice } from "../../types/invoices";
import { useGetInvoicessQuery } from "../../services/invoices.service";
import SubmissionsTable from "../Submissions/Table";
import { useGetSubmissionsQuery } from "../../services/submissions.service";
import { Submission } from "../../types/submissions";
import { useGetEmailsByRefNoQuery } from "../../services/email.service";
import { EmailLog } from "../../types/email";
import EmailEditor from "../common/EmailEditor";
import {
  Add,
  ArrowBack,
  ArrowDropDown,
  Check,
  Close as CloseIcon,
  ContentCopy,
  Edit,
  Phone,
  SendOutlined,
  Warning,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useAppTheme } from "../../context/ThemeContext";

// ── helpers ─────────────────────────────────────────────────────

function val(v: string | undefined | null): React.ReactNode {
  if (!v || v === "N/A") {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
        —
      </Typography>
    );
  }
  return <Typography variant="body2" color="text.secondary">{v}</Typography>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography
        variant="caption"
        fontWeight={600}
        color="text.disabled"
        sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.65rem" }}
      >
        {label}
      </Typography>
      <Box mt={0.25}>{children}</Box>
    </Box>
  );
}

// ── Add menu ─────────────────────────────────────────────────────

function AddMenu({
  student,
  onOpen,
}: {
  student: Student;
  onOpen: (s: Student, type: string) => void;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  return (
    <>
      <Button
        size="small"
        variant="contained"
        startIcon={<Add />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ height: 34 }}
      >
        Add
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => { onOpen(student, "notes"); setAnchor(null); }}>Note</MenuItem>
        <MenuItem onClick={() => { onOpen(student, "services"); setAnchor(null); }}>Service</MenuItem>
        <MenuItem onClick={() => { onOpen(student, "documents"); setAnchor(null); }}>Document</MenuItem>
      </Menu>
    </>
  );
}

// ── Tab panel ────────────────────────────────────────────────────

function TabPanel({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: string;
  index: string;
}) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`tab-${index}`} aria-labelledby={`tab-btn-${index}`}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// ── Tab label with optional count badge ──────────────────────────

function TabLabel({
  label,
  count,
  active,
  accentColor,
}: {
  label: string;
  count?: number;
  active: boolean;
  accentColor: string;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.6}>
      <span>{label}</span>
      {count != null && count > 0 && (
        <Box
          sx={{
            bgcolor: active ? accentColor : "#E2E8F0",
            color: active ? "white" : "#64748B",
            fontSize: "0.58rem",
            fontWeight: 700,
            px: 0.65,
            borderRadius: "10px",
            minWidth: 17,
            height: 15,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            transition: "background-color 0.2s",
          }}
        >
          {count}
        </Box>
      )}
    </Stack>
  );
}

// ── Main ─────────────────────────────────────────────────────────

function StudentDetails() {
  const { data: students } = useGetStudentsQuery("Students");
  const { data: users } = useGetUsersQuery("users");
  const { data: transactions } = useGetTransactionsQuery("Transactions");
  const { data: invoices } = useGetInvoicessQuery("Invoices");
  const { data: allSubmissions } = useGetSubmissionsQuery("Submissions");
  const { id } = useParams();
  const { data: studentEmails } = useGetEmailsByRefNoQuery(
    students?.find((s) => s._id === id)?.refNo ?? "",
    { skip: !students || !id }
  );
  const [updateStudent] = useUpdateStudentMutation();
  const navigate = useNavigate();
  const { isNewTheme, accentColor } = useAppTheme();

  const [student, setStudent] = useState<Student>();
  const [employee, setEmployee] = useState<string>();
  const [studentTransactions, setStudentTransactions] = useState<Transaction[]>();
  const [studentInvoices, setStudentInvoices] = useState<Invoice[]>();
  const [studentSubmissions, setStudentSubmissions] = useState<Submission[]>();
  const [selectedTab, setSelectedTab] = useState(
    () => localStorage.getItem(`student-tab-${id}`) ?? "Profile"
  );
  const [details, setDetails] = useState<{ type: string; student: Student } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [quickNote, setQuickNote] = useState("");
  const quickNoteRef = useRef<HTMLInputElement>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);

  const role = useSelector((state: RootState) => state.user.role);
  const permissions = useSelector((state: RootState) => state.user.permissions);
  const currentUser = useSelector((state: RootState) => state.user);
  const allowedResources = permissions.map((res) =>
    res.actions.includes("READ") ? res.resource : undefined
  );

  useEffect(() => {
    if (students && users) {
      const found = students.find((s) => s._id === id)!;
      setStudent(found);
      setEmployee(users.find((u) => u._id === found?.employee)?.username);
      setStudentTransactions(transactions?.filter((t: Transaction) => t.student === found?._id));
      setStudentInvoices(invoices?.filter((t: Invoice) => t.student === found?._id));
      setStudentSubmissions(allSubmissions?.filter((s: Submission) => s.refNo === found?.refNo));
    }
  }, [id, students, users, transactions, invoices, allSubmissions]);

  // keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;
      if (e.key === "e" || e.key === "E") setEditOpen(true);
      if (e.key === "Escape") navigate("/students");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  if (!student) return <Loader />;

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    localStorage.setItem(`student-tab-${id}`, tab);
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const handleStatusChange = (newStatus: string) => {
    setStatusAnchor(null);
    if (newStatus !== student.status) {
      updateStudent({ ...student, status: newStatus });
    }
  };

  const handleQuickNote = () => {
    if (!quickNote.trim()) return;
    const newNote: Note = {
      title: "Note",
      note: quickNote.trim(),
      createdAt: new Date().toISOString(),
    };
    handleNotesUpdate([newNote, ...(student.additionalData.notes ?? [])]);
    setQuickNote("");
  };

  // document progress
  const docs = student.additionalData?.documents ?? [];
  const docTotal = docs.length * 2;
  const docFilled = docs.reduce(
    (acc, doc: any) =>
      acc + (doc.parts?.arabic?.available ? 1 : 0) + (doc.parts?.english?.available ? 1 : 0),
    0
  );
  const docPct = docTotal > 0 ? Math.round((docFilled / docTotal) * 100) : 0;

  // passport expiry
  const passportExpiry = student.passport?.expiryDate
    ? new Date(student.passport.expiryDate)
    : null;
  const daysToExpiry = passportExpiry
    ? Math.ceil((passportExpiry.getTime() - Date.now()) / 86400000)
    : null;
  const passportExpired = daysToExpiry !== null && daysToExpiry <= 0;
  const passportWarning = daysToExpiry !== null && daysToExpiry > 0 && daysToExpiry <= 90;

  const handleDocumentsUpdate = (documents: Document[]) => {
    updateStudent({ ...student, additionalData: { ...student.additionalData, documents } });
  };
  const handleServicesUpdate = (services: Service[]) => {
    updateStudent({ ...student, additionalData: { ...student.additionalData, services } });
  };
  const handleNotesUpdate = (notes: Note[]) => {
    updateStudent({ ...student, additionalData: { ...student.additionalData, notes } });
  };

  const handleEditSubmit = (updated: Student) => {
    const cleaned = { ...updated } as any;
    for (const key in cleaned) {
      if (cleaned[key] === "" || cleaned[key] === null) delete cleaned[key];
      if (cleaned[key] === "N/A") cleaned[key] = null;
    }
    updateStudent(cleaned as Student).then(() => setEditOpen(false));
  };

  // tab counts
  const tabCounts: Record<string, number> = {
    Documents: student.additionalData?.documents?.length ?? 0,
    Services: student.additionalData?.services?.length ?? 0,
    Notes: student.additionalData?.notes?.length ?? 0,
    Transactions: studentTransactions?.length ?? 0,
    Invoices: studentInvoices?.length ?? 0,
    Submissions: studentSubmissions?.length ?? 0,
    Emails: studentEmails?.length ?? 0,
  };

  const visibleTabs = [
    "Profile",
    "Documents",
    "Services",
    "Notes",
    "Submissions",
    "Transactions",
    "Invoices",
    "Emails",
  ].filter(
    (tab) =>
      allowedResources.includes(tab.toLowerCase()) ||
      ["Profile", "Documents", "Services", "Notes", "Submissions", "Emails"].includes(tab) ||
      role === "admin"
  );

  const statusColor = (CHIPS_COLOR[student.status] ?? "default") as any;
  const allStatuses = Object.values(STUDENT_STATUS);

  return (
    <>
      {details && (
        <StudentAdditionalInfo
          open={!!details}
          handleClose={() => setDetails(null)}
          details={details}
        />
      )}

      {editOpen && (
        <StudentsForm
          open
          edit
          selectedStudent={student}
          users={users ?? []}
          handleClose={() => setEditOpen(false)}
          submitHanlder={handleEditSubmit}
        />
      )}

      {/* Status change menu */}
      <Menu
        anchorEl={statusAnchor}
        open={Boolean(statusAnchor)}
        onClose={() => setStatusAnchor(null)}
        PaperProps={{ sx: { maxHeight: 320, minWidth: 200 } }}
      >
        {allStatuses.map((s) => (
          <MenuItem
            key={s}
            selected={s === student.status}
            onClick={() => handleStatusChange(s)}
            sx={{ fontSize: "0.82rem", gap: 1 }}
          >
            {s === student.status && <Check sx={{ fontSize: 14, color: accentColor }} />}
            <Box component="span" sx={{ ml: s === student.status ? 0 : "22px" }}>
              {s}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Container maxWidth="lg" sx={{ height: "100%", overflow: "auto", pb: 4 }}>

        {/* ── Breadcrumb ─────────────────────────────── */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pt: 2, pb: 1.5 }}>
          <Tooltip title="Back to Students (Esc)" arrow>
            <IconButton size="small" onClick={() => navigate("/students")} sx={{ mr: 0.5 }}>
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>
          <Breadcrumbs sx={{ fontSize: "0.85rem" }}>
            <Link to="/students" style={{ color: "inherit", textDecoration: "none" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ "&:hover": { color: accentColor } }}
              >
                Students
              </Typography>
            </Link>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              {student.fullName}
            </Typography>
          </Breadcrumbs>
        </Stack>

        {/* ── Header card ────────────────────────────── */}
        <Card
          sx={
            isNewTheme
              ? { borderRadius: "10px", border: "1px solid #E2E8F0", boxShadow: "0 2px 8px rgba(15,23,42,0.06)", mb: 2 }
              : { borderRadius: "10px", boxShadow: "0px 0px 55px 5px rgba(0,0,0,0.05)", mb: 2 }
          }
        >
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Stack direction="row" spacing={2.5} alignItems="flex-start">

              {/* Avatar */}
              <Avatar
                sx={{
                  width: 68,
                  height: 68,
                  bgcolor: accentColor,
                  fontSize: "1.4rem",
                  fontWeight: 700,
                  flexShrink: 0,
                  boxShadow: `0 0 0 3px ${accentColor}22`,
                }}
              >
                {student.fullName?.slice(0, 2).toUpperCase()}
              </Avatar>

              {/* Name + meta */}
              <Box flex={1} minWidth={0}>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5} flexWrap="wrap">
                  <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {student.fullName}
                  </Typography>

                  {/* Clickable status chip */}
                  <Tooltip title="Change status" arrow>
                    <Chip
                      label={
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                          <span>{student.status}</span>
                          <ArrowDropDown sx={{ fontSize: 16, ml: "-2px" }} />
                        </Stack>
                      }
                      color={statusColor}
                      size="small"
                      onClick={(e) => setStatusAnchor(e.currentTarget)}
                      sx={{
                        borderRadius: "4px",
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        height: 22,
                        cursor: "pointer",
                        "& .MuiChip-label": { pr: 0.5 },
                      }}
                    />
                  </Tooltip>

                  {/* Passport expiry alert */}
                  {passportExpired && (
                    <Chip
                      icon={<Warning sx={{ fontSize: "13px !important" }} />}
                      label="Passport expired"
                      size="small"
                      color="error"
                      sx={{ borderRadius: "4px", fontSize: "0.7rem", height: 22, fontWeight: 600 }}
                    />
                  )}
                  {passportWarning && (
                    <Chip
                      icon={<Warning sx={{ fontSize: "13px !important" }} />}
                      label={`Passport expires in ${daysToExpiry}d`}
                      size="small"
                      color="warning"
                      sx={{ borderRadius: "4px", fontSize: "0.7rem", height: 22, fontWeight: 600 }}
                    />
                  )}
                </Stack>

                {student.email && (
                  <Stack direction="row" alignItems="center" spacing={0.25} mb={1.25}>
                    <Typography variant="body2" color="text.secondary">
                      {student.email}
                    </Typography>
                    <Tooltip title={copiedField === "email" ? "Copied!" : "Copy email"} arrow>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(student.email, "email")}
                        sx={{ width: 18, height: 18, color: "#CBD5E1", "&:hover": { color: "#64748B" } }}
                      >
                        {copiedField === "email" ? (
                          <Check sx={{ fontSize: "0.7rem", color: "#059669" }} />
                        ) : (
                          <ContentCopy sx={{ fontSize: "0.7rem" }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}

                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
                  {/* Ref — copyable */}
                  <Chip
                    label={`Ref: ${student.refNo}`}
                    size="small"
                    variant="outlined"
                    deleteIcon={
                      <Tooltip title={copiedField === "ref" ? "Copied!" : "Copy"} arrow>
                        {copiedField === "ref" ? (
                          <Check sx={{ fontSize: "12px !important", color: "#059669 !important" }} />
                        ) : (
                          <ContentCopy sx={{ fontSize: "12px !important" }} />
                        )}
                      </Tooltip>
                    }
                    onDelete={() => handleCopy(student.refNo, "ref")}
                    sx={{ borderRadius: "4px", fontSize: "0.72rem", fontFamily: "monospace" }}
                  />

                  {/* Passport — copyable */}
                  {student.passport?.number && (
                    <Chip
                      label={`Passport: ${student.passport.number}`}
                      size="small"
                      variant="outlined"
                      deleteIcon={
                        <Tooltip title={copiedField === "passport" ? "Copied!" : "Copy"} arrow>
                          {copiedField === "passport" ? (
                            <Check sx={{ fontSize: "12px !important", color: "#059669 !important" }} />
                          ) : (
                            <ContentCopy sx={{ fontSize: "12px !important" }} />
                          )}
                        </Tooltip>
                      }
                      onDelete={() => handleCopy(student.passport.number, "passport")}
                      sx={{ borderRadius: "4px", fontSize: "0.72rem" }}
                    />
                  )}

                  {/* Phone — call + copy + WhatsApp */}
                  {student.phone && (
                    <>
                      <Chip
                        component="a"
                        href={`tel:${student.phone}`}
                        label={student.phone}
                        size="small"
                        icon={<Phone sx={{ fontSize: "14px !important" }} />}
                        deleteIcon={
                          <Tooltip title={copiedField === "phone" ? "Copied!" : "Copy"} arrow>
                            {copiedField === "phone" ? (
                              <Check sx={{ fontSize: "12px !important", color: "#059669 !important" }} />
                            ) : (
                              <ContentCopy sx={{ fontSize: "12px !important" }} />
                            )}
                          </Tooltip>
                        }
                        onDelete={() => handleCopy(student.phone, "phone")}
                        clickable
                        sx={{ borderRadius: "4px", fontSize: "0.72rem" }}
                      />
                      <Chip
                        component="a"
                        href={`https://wa.me/${student.phone.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        label="WhatsApp"
                        size="small"
                        clickable
                        sx={{
                          borderRadius: "4px",
                          fontSize: "0.72rem",
                          bgcolor: "#25D366",
                          color: "white",
                          "&:hover": { bgcolor: "#1ebe5d" },
                        }}
                      />
                    </>
                  )}

                  {(student as any).commission > 0 && (
                    <Chip
                      label={`RM ${Number((student as any).commission).toLocaleString()}`}
                      size="small"
                      color="success"
                      sx={{ borderRadius: "4px", fontSize: "0.72rem", fontWeight: 600 }}
                    />
                  )}
                </Stack>

                {/* Document progress */}
                {docTotal > 0 && (
                  <Stack direction="row" alignItems="center" spacing={1} mt={1.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                      Docs {docFilled}/{docTotal}
                    </Typography>
                    <Tooltip title={`${docPct}% complete`} arrow>
                      <LinearProgress
                        variant="determinate"
                        value={docPct}
                        sx={{
                          width: 100,
                          height: 5,
                          borderRadius: 999,
                          bgcolor: "#E2E8F0",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                            bgcolor: docPct === 100 ? "#059669" : docPct >= 50 ? accentColor : "#EAB308",
                          },
                        }}
                      />
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      {docPct}%
                    </Typography>
                  </Stack>
                )}
              </Box>

              {/* Actions */}
              <Stack direction="row" spacing={1} flexShrink={0}>
                <Tooltip title="Edit (E)" arrow>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit sx={{ fontSize: 15 }} />}
                    onClick={() => setEditOpen(true)}
                    sx={{ height: 34, fontWeight: 600, fontSize: "0.8rem" }}
                  >
                    Edit
                  </Button>
                </Tooltip>
                <AddMenu student={student} onOpen={(s, type) => setDetails({ type, student: s })} />
              </Stack>
            </Stack>

            {/* Meta row */}
            <Stack
              direction="row"
              spacing={3}
              mt={2}
              pt={2}
              flexWrap="wrap"
              sx={{ borderTop: `1px solid ${isNewTheme ? "#F1F5F9" : "rgba(0,0,0,0.08)"}`, gap: 1 }}
            >
              <Typography variant="caption" color="text.disabled">
                Handled by{" "}
                <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>
                  {employee ?? "—"}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Added{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  {formatDate(student.createdAt)}
                </Typography>
              </Typography>
              {student.updatedAt && (
                <Typography variant="caption" color="text.disabled">
                  Updated{" "}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {formatDate(student.updatedAt)}
                  </Typography>
                </Typography>
              )}
              {student.nationality && (
                <Typography variant="caption" color="text.disabled">
                  Nationality{" "}
                  <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>
                    {student.nationality}
                  </Typography>
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* ── Tabs (sticky) ──────────────────────────── */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            bgcolor: "background.default",
            borderBottom: 1,
            borderColor: "divider",
            mx: -0.5,
            px: 0.5,
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={(_, v) => handleTabChange(v)}
            aria-label="student tabs"
          >
            {visibleTabs.map((tab) => (
              <Tab
                key={tab}
                label={
                  <TabLabel
                    label={tab}
                    count={tabCounts[tab]}
                    active={selectedTab === tab}
                    accentColor={accentColor}
                  />
                }
                value={tab}
                id={`tab-btn-${tab}`}
                aria-controls={`tab-${tab}`}
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: selectedTab === tab ? 700 : 400,
                  textTransform: "none",
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* ── Profile ────────────────────────────────── */}
        <TabPanel value={selectedTab} index="Profile">
          <Grid container spacing={3}>
            {/* Personal info */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                fontWeight={700}
                color="text.disabled"
                sx={{ letterSpacing: "0.1em" }}
              >
                Personal
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 2 }} />
              <Stack spacing={2}>
                <Grid container rowSpacing={2} columnSpacing={3}>
                  <Grid item xs={6}>
                    <Field label="Full Name">{val(student.fullName)}</Field>
                  </Grid>
                  <Grid item xs={6}>
                    <Field label="Arabic Name">{val(student.arabicName)}</Field>
                  </Grid>
                  <Grid item xs={6}>
                    <Field label="Phone Number">
                      {student.phone ? (
                        <Typography
                          component="a"
                          href={`tel:${student.phone}`}
                          variant="body2"
                          color="text.secondary"
                          sx={{ textDecoration: "none", "&:hover": { color: accentColor } }}
                        >
                          {student.phone}
                        </Typography>
                      ) : (
                        val(undefined)
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={6}>
                    <Field label="Address">{val(student.address)}</Field>
                  </Grid>
                  <Grid item xs={6}>
                    <Field label="Nationality">{val(student.nationality)}</Field>
                  </Grid>
                  <Grid item xs={6}>
                    <Field label="Degree">{val(student.degree)}</Field>
                  </Grid>
                  <Grid item xs={6}>
                    <Field label="Program">{val(student.program)}</Field>
                  </Grid>
                  <Grid item xs={6}>
                    <Field label="University">{val(student.university)}</Field>
                  </Grid>
                </Grid>
              </Stack>
            </Grid>

            {/* Passport */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                fontWeight={700}
                color="text.disabled"
                sx={{ letterSpacing: "0.1em" }}
              >
                Passport
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 2 }} />
              <Grid container rowSpacing={2} columnSpacing={3}>
                <Grid item xs={12}>
                  <Field label="Passport Number">{val(student.passport?.number)}</Field>
                </Grid>
                <Grid item xs={6}>
                  <Field label="Issue Date">
                    {val(formatDate(student.passport?.issueDate) as string)}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field label="Expiry Date">
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      {val(formatDate(student.passport?.expiryDate) as string)}
                      {passportExpired && (
                        <Typography variant="caption" color="error" fontWeight={600}>
                          Expired
                        </Typography>
                      )}
                      {passportWarning && (
                        <Typography variant="caption" sx={{ color: "#D97706" }} fontWeight={600}>
                          {daysToExpiry}d left
                        </Typography>
                      )}
                    </Stack>
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field label="Birth Date">
                    {val(formatDate(student.passport?.birthDate) as string)}
                  </Field>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </TabPanel>

        {/* ── Documents ──────────────────────────────── */}
        <TabPanel value={selectedTab} index="Documents">
          <Documents documents={student.additionalData.documents} onUpdate={handleDocumentsUpdate} />
        </TabPanel>

        {/* ── Services ───────────────────────────────── */}
        <TabPanel value={selectedTab} index="Services">
          <Services services={student.additionalData.services} onUpdate={handleServicesUpdate} />
        </TabPanel>

        {/* ── Notes (with quick-add) ─────────────────── */}
        <TabPanel value={selectedTab} index="Notes">
          <Stack
            direction="row"
            spacing={1}
            mb={2.5}
            component="form"
            onSubmit={(e) => { e.preventDefault(); handleQuickNote(); }}
          >
            <TextField
              inputRef={quickNoteRef}
              size="small"
              placeholder="Add a quick note…"
              value={quickNote}
              onChange={(e) => setQuickNote(e.target.value)}
              fullWidth
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
            <Tooltip title="Add note" arrow>
              <span>
                <IconButton
                  type="submit"
                  disabled={!quickNote.trim()}
                  sx={{
                    bgcolor: accentColor,
                    color: "white",
                    borderRadius: "8px",
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    "&:hover": { bgcolor: accentColor, opacity: 0.85 },
                    "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
                  }}
                >
                  <SendOutlined sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Notes notes={student.additionalData.notes} onUpdate={handleNotesUpdate} />
        </TabPanel>

        {/* ── Submissions ────────────────────────────── */}
        <TabPanel value={selectedTab} index="Submissions">
          <SubmissionsTable users={users!} rows={studentSubmissions ?? []} />
        </TabPanel>

        {/* ── Transactions ───────────────────────────── */}
        <TabPanel value={selectedTab} index="Transactions">
          <TransactionsTable students={[student]} users={users!} rows={studentTransactions!} />
        </TabPanel>

        {/* ── Invoices ───────────────────────────────── */}
        <TabPanel value={selectedTab} index="Invoices">
          <InvoicesTable students={[student]} users={users!} rows={studentInvoices!} />
        </TabPanel>

        {/* ── Emails ─────────────────────────────────── */}
        <TabPanel value={selectedTab} index="Emails">
          <Stack direction="row" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              size="small"
              startIcon={<SendOutlined sx={{ fontSize: 15 }} />}
              onClick={() => setEmailOpen(true)}
              sx={{ textTransform: "none", fontWeight: 600, bgcolor: accentColor, "&:hover": { bgcolor: accentColor, opacity: 0.9 } }}
            >
              Compose
            </Button>
          </Stack>
          {!studentEmails || studentEmails.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", py: 4, fontStyle: "italic" }}>
              No emails sent yet
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {studentEmails.map((email) => (
                <Box
                  key={email._id}
                  onClick={() => setSelectedEmail(email)}
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    border: "1px solid #E2E8F0",
                    bgcolor: email.status === "failed" ? "#FFF5F5" : "#FAFBFC",
                    cursor: "pointer",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                    "&:hover": {
                      borderColor: accentColor + "66",
                      boxShadow: `0 0 0 3px ${accentColor}12`,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" fontWeight={700}>
                      {email.subject}
                    </Typography>
                    <Chip
                      label={email.status}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        bgcolor: email.status === "sent" ? "#D1FAE5" : "#FEE2E2",
                        color: email.status === "sent" ? "#059669" : "#EF4444",
                      }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2} mb={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>To:</strong> {email.to}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>From:</strong> {email.from}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.disabled">
                    {formatDate(email.createdAt)}
                  </Typography>
                  {email.status === "failed" && email.errorMessage && (
                    <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                      Error: {email.errorMessage}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </TabPanel>

      </Container>

      {emailOpen && (
        <EmailEditor
          open
          details={{ email: student.email }}
          currentUser={currentUser as any}
          handleClose={() => setEmailOpen(false)}
          refNo={student.refNo}
        />
      )}

      {/* ── Email viewer ───────────────────────────── */}
      <Dialog
        open={!!selectedEmail}
        onClose={() => setSelectedEmail(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "10px", overflow: "hidden" } }}
      >
        {selectedEmail && (
          <>
            <DialogTitle sx={{ pb: 1, pr: 6 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {selectedEmail.subject}
              </Typography>
              <Stack direction="row" spacing={2} mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  <strong>From:</strong> {selectedEmail.from}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <strong>To:</strong> {selectedEmail.to}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {formatDate(selectedEmail.createdAt)}
                </Typography>
                <Chip
                  label={selectedEmail.status}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    bgcolor: selectedEmail.status === "sent" ? "#D1FAE5" : "#FEE2E2",
                    color: selectedEmail.status === "sent" ? "#059669" : "#EF4444",
                  }}
                />
              </Stack>
              <IconButton
                onClick={() => setSelectedEmail(null)}
                size="small"
                sx={{ position: "absolute", right: 12, top: 12, color: "grey.500" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 0 }}>
              {selectedEmail.html ? (
                <iframe
                  title="email-body"
                  srcDoc={selectedEmail.html}
                  style={{ width: "100%", minHeight: 420, border: "none", display: "block" }}
                  sandbox="allow-same-origin"
                />
              ) : (
                <Box sx={{ p: 3, whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "0.875rem", color: "text.primary" }}>
                  {selectedEmail.text || <Typography color="text.disabled" fontStyle="italic">No body content</Typography>}
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
}

export default StudentDetails;
